import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { ExpandCollapseIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ExpandCollapseIcon/ExpandCollapseIcon';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { AddItemIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIcon';
import { CreateWebsitePopup } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateWebsitePopup/CreateWebsitePopup';
import { DeleteKnowledgeBaseIntegrationModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/delete/DeleteKnowledgeBaseIntegrationModal/DeleteKnowledgeBaseIntegrationModal';
import { EditWebsitePopup } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/edit/EditWebsitePopup/EditWebsitePopup';
import { Separator } from 'src/components/ui/separator';
import { AddItemIconDisabled } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIconDisabled';
import { API_VERSION } from 'src/constants/api-routes';
import type { KnowledgeBase, KnowledgeBaseCollector } from 'src/services/knowledgeBaseService';
import { getApiVersion } from 'src/utils/apiVersion';

export const ScrapeWebsiteAccordion = ({
  items = [],
  isButtonsDisabled = false,
  knowledgeBase,
}: {
  items?: KnowledgeBaseCollector[];
  isButtonsDisabled?: boolean;
  knowledgeBase: Pick<KnowledgeBase, 'id'>;
}) => {
  const [value, setValue] = React.useState('');
  const disabled = !items.length;
  const [firstExpanded, setFirstExpanded] = React.useState(false);
  const version = getApiVersion();

  useEffect(() => {
    if (items.length && !firstExpanded && !value) {
      setFirstExpanded(true);
      setValue('new-card');
    }
  }, [items]);

  return (
    <div className={clsx('w-full flex flex-row items-start gap-5')}>
      <div className={'pt-5'}>
        <CheckIcon className={clsx(!disabled ? 'visible' : 'invisible')} />
      </div>
      <Accordion
        value={value}
        onValueChange={setValue}
        type="single"
        className="w-full max-w-[455px] min-w-[455px]"
        collapsible={true}
        disabled={disabled}
      >
        <AccordionItem
          value="new-card"
          className={clsx(
            'group border border-[#B8C1C633] rounded-sm-5 bg-white transition-shadow shadow-none',
            !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
          )}
        >
          <AccordionTrigger hideDefaultIcon className="py-2.5 px-5">
            <div className="flex items-center justify-between w-full">
              <div className={'flex flex-row gap-[7px]'}>
                <div className={'pt-[2px]'}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                  >
                    <path
                      d="M11.77 10C11.83 9.505 11.875 9.01 11.875 8.5C11.875 7.99 11.83 7.495 11.77 7H14.305C14.425 7.48 14.5 7.9825 14.5 8.5C14.5 9.0175 14.425 9.52 14.305 10M10.4425 14.17C10.8925 13.3375 11.2375 12.4375 11.4775 11.5H13.69C12.9634 12.7512 11.8106 13.699 10.4425 14.17ZM10.255 10H6.745C6.67 9.505 6.625 9.01 6.625 8.5C6.625 7.99 6.67 7.4875 6.745 7H10.255C10.3225 7.4875 10.375 7.99 10.375 8.5C10.375 9.01 10.3225 9.505 10.255 10ZM8.5 14.47C7.8775 13.57 7.375 12.5725 7.0675 11.5H9.9325C9.625 12.5725 9.1225 13.57 8.5 14.47ZM5.5 5.5H3.31C4.02898 4.24528 5.18099 3.29593 6.55 2.83C6.1 3.6625 5.7625 4.5625 5.5 5.5ZM3.31 11.5H5.5C5.7625 12.4375 6.1 13.3375 6.55 14.17C5.1837 13.6992 4.03322 12.7511 3.31 11.5ZM2.695 10C2.575 9.52 2.5 9.0175 2.5 8.5C2.5 7.9825 2.575 7.48 2.695 7H5.23C5.17 7.495 5.125 7.99 5.125 8.5C5.125 9.01 5.17 9.505 5.23 10M8.5 2.5225C9.1225 3.4225 9.625 4.4275 9.9325 5.5H7.0675C7.375 4.4275 7.8775 3.4225 8.5 2.5225ZM13.69 5.5H11.4775C11.2424 4.57121 10.8948 3.67458 10.4425 2.83C11.8225 3.3025 12.97 4.255 13.69 5.5ZM8.5 1C4.3525 1 1 4.375 1 8.5C1 10.4891 1.79018 12.3968 3.1967 13.8033C3.89314 14.4997 4.71993 15.0522 5.62987 15.4291C6.53982 15.806 7.51509 16 8.5 16C10.4891 16 12.3968 15.2098 13.8033 13.8033C15.2098 12.3968 16 10.4891 16 8.5C16 7.51509 15.806 6.53982 15.4291 5.62987C15.0522 4.71993 14.4997 3.89314 13.8033 3.1967C13.1069 2.50026 12.2801 1.94781 11.3701 1.5709C10.4602 1.19399 9.48491 1 8.5 1Z"
                      fill="#05050D"
                    />
                    <path
                      d="M11.77 10C11.83 9.505 11.875 9.01 11.875 8.5C11.875 7.99 11.83 7.495 11.77 7H14.305C14.425 7.48 14.5 7.9825 14.5 8.5C14.5 9.0175 14.425 9.52 14.305 10M2.695 10C2.575 9.52 2.5 9.0175 2.5 8.5C2.5 7.9825 2.575 7.48 2.695 7H5.23C5.17 7.495 5.125 7.99 5.125 8.5C5.125 9.01 5.17 9.505 5.23 10M10.4425 14.17C10.8925 13.3375 11.2375 12.4375 11.4775 11.5H13.69C12.9634 12.7512 11.8106 13.699 10.4425 14.17ZM10.255 10H6.745C6.67 9.505 6.625 9.01 6.625 8.5C6.625 7.99 6.67 7.4875 6.745 7H10.255C10.3225 7.4875 10.375 7.99 10.375 8.5C10.375 9.01 10.3225 9.505 10.255 10ZM8.5 14.47C7.8775 13.57 7.375 12.5725 7.0675 11.5H9.9325C9.625 12.5725 9.1225 13.57 8.5 14.47ZM5.5 5.5H3.31C4.02898 4.24528 5.18099 3.29593 6.55 2.83C6.1 3.6625 5.7625 4.5625 5.5 5.5ZM3.31 11.5H5.5C5.7625 12.4375 6.1 13.3375 6.55 14.17C5.1837 13.6992 4.03322 12.7511 3.31 11.5ZM8.5 2.5225C9.1225 3.4225 9.625 4.4275 9.9325 5.5H7.0675C7.375 4.4275 7.8775 3.4225 8.5 2.5225ZM13.69 5.5H11.4775C11.2424 4.57121 10.8948 3.67458 10.4425 2.83C11.8225 3.3025 12.97 4.255 13.69 5.5ZM8.5 1C4.3525 1 1 4.375 1 8.5C1 10.4891 1.79018 12.3968 3.1967 13.8033C3.89314 14.4997 4.71993 15.0522 5.62987 15.4291C6.53982 15.806 7.51509 16 8.5 16C10.4891 16 12.3968 15.2098 13.8033 13.8033C15.2098 12.3968 16 10.4891 16 8.5C16 7.51509 15.806 6.53982 15.4291 5.62987C15.0522 4.71993 14.4997 3.89314 13.8033 3.1967C13.1069 2.50026 12.2801 1.94781 11.3701 1.5709C10.4602 1.19399 9.48491 1 8.5 1Z"
                      stroke="white"
                      strokeWidth="0.1"
                    />
                  </svg>
                </div>
                <div className={'text-start'}>
                  <p className={'text-ebony text-sm font-semibold'}>
                    Scrape your website{' '}
                    <span
                      className={clsx(
                        'text-base-xs font-medium text-[#7A6BE5] bg-[#A498FF1A] px-[9px] py-[2px] rounded-[50px] ml-2.5',
                        disabled && 'invisible',
                      )}
                    >
                      {items.length}
                    </span>
                  </p>
                  <p className={'text-ebony_opacity_30 text-base-xs font-medium'}>
                    Let the AI learn from your public pages
                  </p>
                </div>
              </div>
              {!disabled && (
                <ExpandCollapseIcon
                  className={clsx(
                    'w-[12px] opacity-50 rotate-0 transition group-hover:opacity-100',
                    value && 'rotate-180',
                  )}
                />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-0 max-w-[454px]">
            {items.map((item, index, array) => {
              return (
                <div key={item.id}>
                  <div
                    className={
                      '-mx-5 pr-5 py-4 bg-[#F1F6F6] flex flex-row pl-[45px] items-center justify-between gap-4'
                    }
                  >
                    <div
                      className={
                        'block max-w-[84%] min-w-0 overflow-hidden whitespace-nowrap text-ellipsis'
                      }
                    >
                      {version === API_VERSION.V1 && item?.urls && (
                        <a
                          className={
                            'text-base-xs text-ebony font-medium underline self-start text-ellipsis '
                          }
                          href={item.urls[0]}
                          target={'_blank'}
                          rel={'noopener noreferrer'}
                        >
                          {item.urls[0]}
                        </a>
                      )}
                      {version === API_VERSION.V2 && item?.url && (
                        <a
                          className={
                            'text-base-xs text-ebony font-medium underline self-start text-ellipsis '
                          }
                          href={item.url}
                          target={'_blank'}
                          rel={'noopener noreferrer'}
                        >
                          {item.url}
                        </a>
                      )}
                    </div>
                    <div className={'flex flex-row gap-7'}>
                      <div
                        className={'flex items-center opacity-30 hover:opacity-100 cursor-pointer'}
                      >
                        <EditWebsitePopup
                          item={{ ...item, data: item }}
                          knowledgeBaseId={knowledgeBase.id}
                          trigger={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 15 15"
                              fill="none"
                            >
                              <path
                                d="M1.66667 13.3306H2.845L10.6067 5.55634L9.42833 4.37609L1.66667 12.1504V13.3306ZM15 15H0V11.4584L11.1958 0.244385C11.3521 0.0879054 11.564 0 11.785 0C12.006 0 12.2179 0.0879054 12.3742 0.244385L14.7317 2.60572C14.8879 2.76224 14.9757 2.97451 14.9757 3.19584C14.9757 3.41717 14.8879 3.62944 14.7317 3.78597L5.2025 13.3306H15V15ZM10.6067 3.19584L11.785 4.37609L12.9633 3.19584L11.785 2.01559L10.6067 3.19584Z"
                                fill="#05050D"
                              />
                            </svg>
                          }
                        />
                      </div>
                      <div className={'flex items-center'}>
                        <DeleteKnowledgeBaseIntegrationModal
                          knowledgeBaseId={knowledgeBase.id}
                          item={{ ...item, data: item }}
                        />
                      </div>
                    </div>
                  </div>
                  {index !== array.length - 1 && (
                    <Separator className={'h-[1px] bg-[#b8c1c657] -mx-5'} />
                  )}
                </div>
              );
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className={clsx('pt-3.5 group hover:cursor-pointer')}>
        {isButtonsDisabled ? (
          <AddItemIconDisabled />
        ) : (
          <CreateWebsitePopup
            knowledgeBaseId={knowledgeBase.id}
            trigger={
              <AddItemIcon
                className={clsx(isButtonsDisabled && 'pointer-events-none cursor-default')}
              />
            }
            disabled={isButtonsDisabled}
            initialRequest={{ run_immediately: false }}
          />
        )}
      </div>
    </div>
  );
};
